// Owned by task 4.2 of the dashboard-redesign spec (Requirement 6).
//
// KPI tile that displays the current-period service-charge collection rate
// alongside the demanded / collected monetary totals. Data comes from the
// `service-charge-reconciliation` spec via `useCollectionRate(scope)`; this
// widget is READ-ONLY (Requirement 15.3) and re-aggregates nothing that the
// General Ledger owns.
//
// See:
//   - `.kiro/specs/dashboard-redesign/requirements.md` §Requirement 6
//   - `.kiro/specs/dashboard-redesign/design.md` §Widget catalog #2
//   - `.kiro/specs/dashboard-redesign/design-mockup.svg` (Widget 2)
//   - `.kiro/steering/design-language.md` (palette, monetary display)
//
// State handling (Requirement 3.2):
//   loading → <WidgetSkeleton> (via <WidgetFrame>)
//   empty   → "No collection data" copy (via <WidgetFrame>)
//   ready   → the % KPI + demanded / collected caption line
//   error   → <WidgetError> with the ErrorId (via <WidgetFrame>)
//
// A local <ErrorBoundary> wraps the ready-state tree so a render-time
// exception in this widget stays scoped and does not blank the page
// (Requirement 3.6). The boundary hands a synthetic `errorId` to
// <WidgetError> so a treasurer can still quote a value to support.

import React from "react";
import { Link } from "react-router-dom";
import { ErrorBoundary } from "../../../components/ui/ErrorBoundary";
import { WidgetError } from "../WidgetError";
import { WidgetFrame } from "../WidgetFrame";
import { useCollectionRate } from "../hooks/useCollectionRate";
import { formatMoneyGBP } from "../formatMoney";
import type { BuildingScope, CollectionRate } from "../types";

/** Stable header copy — mirrors the design mockup. */
const WIDGET_TITLE = "Collection Rate";

/** Small caps label above the KPI value; matches design-mockup.svg. */
const HEADER_LABEL = "SERVICE-CHARGE COLLECTION";

/** Copy shown in the empty state (task 4.2, DONE-WHEN clause). */
const EMPTY_COPY = "No collection data";

/**
 * Deep-link to the owning spec's report page. `service-charge-reconciliation`
 * derives collection rate from the GL per Constitution Rule 2; its user-
 * facing view lives at `/dashboard/reports/service-charge` (see
 * `frontend/src/pages/reports/ReportsHub.tsx`).
 */
export const COLLECTION_RATE_DEEP_LINK = "/dashboard/reports/service-charge";

/**
 * Colour thresholds for the KPI value.
 *
 * Task 4.2:
 *   green  when rate >= 90%
 *   amber  when 70% <= rate <  90%
 *   red    when rate <  70%
 *
 * The 90% cutover is inclusive of green; the 70% cutover is inclusive of
 * amber. The three tokens match `.kiro/steering/design-language.md`.
 */
export const COLOUR_HEALTHY = "#16a34a";
export const COLOUR_WARNING = "#f59e0b";
export const COLOUR_CRITICAL = "#dc2626";

const THRESHOLD_HEALTHY = 0.9;
const THRESHOLD_WARNING = 0.7;

/**
 * Map a rate in [0, 1] to its health colour. Values outside the interval
 * are clamped by the mapping: anything at or above 90% is green, anything
 * below 70% is red. The hook (`useCollectionRate`) is already coded to
 * reject payloads with rate outside [0, 1] as errors, so this function is
 * only ever called with valid values on the ready path.
 */
export function getCollectionRateColour(rate: number): string {
  if (rate >= THRESHOLD_HEALTHY) return COLOUR_HEALTHY;
  if (rate >= THRESHOLD_WARNING) return COLOUR_WARNING;
  return COLOUR_CRITICAL;
}

/**
 * Format a rate in [0, 1] as a percentage with exactly one decimal place
 * (Requirement 6.2 — e.g. `78.4%`). Conversion happens ONLY at the display
 * boundary; the raw `rate` value from the hook is never bound to the DOM.
 */
export function formatCollectionRatePercent(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

export interface WidgetCollectionRateProps {
  scope: BuildingScope;
}

/**
 * Render the ready-state contents. Extracted so `<WidgetFrame>` only
 * renders it when `result.status === "ready"`; other states are handled by
 * the frame itself (skeleton / empty / error card).
 */
function ReadyContent({ data }: { data: CollectionRate }): JSX.Element {
  const rateColour = getCollectionRateColour(data.rate);
  const percentText = formatCollectionRatePercent(data.rate);
  const collectedText = formatMoneyGBP(data.collectedPence);
  const demandedText = formatMoneyGBP(data.demandedPence);

  return (
    <Link
      to={COLLECTION_RATE_DEEP_LINK}
      data-testid="widget-collection-rate-link"
      aria-label={`${WIDGET_TITLE}: ${percentText}`}
      className="block w-full h-full px-5 py-4 rounded-xl border transition-colors hover:bg-gray-50"
      style={{ backgroundColor: "#ffffff", borderColor: "#e5e7eb" }}
    >
      <div
        className="text-xs font-semibold uppercase tracking-wide"
        style={{ color: "#6b7280" }}
      >
        {HEADER_LABEL}
      </div>
      <div
        data-testid="widget-collection-rate-value"
        className="mt-2 text-3xl font-bold"
        style={{ color: rateColour }}
      >
        {percentText}
      </div>
      <div
        data-testid="widget-collection-rate-period"
        className="mt-1 text-xs"
        style={{ color: "#9ca3af" }}
      >
        {data.periodLabel}
      </div>
      <div
        data-testid="widget-collection-rate-amounts"
        className="mt-2 text-xs"
        style={{ color: "#6b7280" }}
      >
        <span data-testid="widget-collection-rate-collected">
          {collectedText}
        </span>
        {" collected of "}
        <span data-testid="widget-collection-rate-demanded">
          {demandedText}
        </span>
        {" demanded"}
      </div>
    </Link>
  );
}

/**
 * The `Widget_CollectionRate` KPI tile. Composes `useCollectionRate` with
 * the shared `<WidgetFrame>` state machine and wraps the whole tree in an
 * `<ErrorBoundary>` so a render exception here cannot bubble up and blank
 * the dashboard.
 */
export function WidgetCollectionRate({
  scope,
}: WidgetCollectionRateProps): JSX.Element {
  const result = useCollectionRate(scope);

  return (
    <ErrorBoundary
      fallback={(err) => (
        <WidgetError
          name={WIDGET_TITLE}
          errorId={`render-${err.name || "Error"}`}
        />
      )}
    >
      <WidgetFrame
        result={result}
        name={WIDGET_TITLE}
        emptyCopy={EMPTY_COPY}
        skeletonHeight={140}
      >
        {result.status === "ready" ? <ReadyContent data={result.data} /> : null}
      </WidgetFrame>
    </ErrorBoundary>
  );
}

// Re-export under the underscore name used in requirements.md and tasks.md
// (`Widget_CollectionRate`) so imports can match either convention.
export { WidgetCollectionRate as Widget_CollectionRate };

export default WidgetCollectionRate;
